// src/shared/components/ui/ConfirmModal/ConfirmModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button';
import { ModalCenter } from '../ModalCenter';
import type { ModalCenterRef } from '../ModalCenter/types';
import { EnterIcon, EscIcon } from '../icons';
import { Spinner } from '../Spinner';

export interface ConfirmModalRef {
  /**
   * Abre la modal de confirmación.
   * @param onConfirm Callback que se ejecutará si el usuario acepta.
   * @param onCancel Callback que se ejecutará si el usuario cancela (opcional).
   * @param title Título de la modal (opcional).
   * @param message Mensaje de la modal (opcional).
   * @param confirmText Texto del botón de confirmar (opcional).
   * @param cancelText Texto del botón de cancelar (opcional).
   */
  open: (
    onConfirm: () => unknown,
    onCancel?: () => void,
    title?: string,
    message?: string,
    confirmText?: string,
    cancelText?: string,
    renderContent?: () => React.ReactNode,
  ) => void;
}

export const ConfirmModal = forwardRef<ConfirmModalRef>((_, ref) => {
  const { t } = useTranslation('ui');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<() => unknown>(() => {});
  const cancelRef = useRef<(() => void) | undefined>(undefined);
  const isConfirmingRef = useRef(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    renderContent?: () => React.ReactNode;
  }>({
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
  });

  useImperativeHandle(ref, () => ({
    open: (
      onConfirm: () => unknown,
      onCancel?: () => void,
      title?: string,
      message?: string,
      confirmText?: string,
      cancelText?: string,
      renderContent?: () => React.ReactNode,
    ) => {
      confirmRef.current = onConfirm;
      cancelRef.current = onCancel;
      isConfirmingRef.current = false;
      setIsConfirming(false);
      setModalConfig({
        title: title || t('confirmModal.defaultTitle'),
        message: message || t('confirmModal.defaultMessage'),
        confirmText: confirmText || t('confirmModal.defaultConfirmText'),
        cancelText: cancelText || t('confirmModal.defaultCancelText'),
        renderContent,
      });
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = async () => {
    if (isConfirmingRef.current) {
      return;
    }

    isConfirmingRef.current = true;
    setIsConfirming(true);

    try {
      await confirmRef.current();
      confirmRef.current = () => {};
      cancelRef.current = undefined;
      modalRef.current?.closeModal();
    } finally {
      isConfirmingRef.current = false;
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    if (isConfirmingRef.current) {
      return;
    }

    // Siempre ejecutar el callback de cancelación si existe
    if (cancelRef.current) {
      cancelRef.current();
    }
    // Limpiar referencias
    confirmRef.current = () => {};
    cancelRef.current = undefined;
    // Siempre cerrar la modal
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter ref={modalRef} title={modalConfig.title} type="default" width="default" preventClose={isConfirming}>
      <div className="space-y-4">
        <p className="text-sm whitespace-pre-line">{modalConfig.message}</p>
        {modalConfig.renderContent?.()}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={handleCancel} disabled={isConfirming}>
            {modalConfig.cancelText}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            loading={isConfirming}
            disabled={isConfirming}
            data-testid="confirm-modal-confirm"
          >
            {isConfirming ? (
              <Spinner size="sm" className="p-0" />
            ) : (
              <>
                {modalConfig.confirmText}
                <EnterIcon className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ConfirmModal.displayName = 'ConfirmModal';
